import { addDays, addHours, addWeeks } from 'date-fns';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

import { t } from '@lib/translation';
import { SnFormFieldConfig } from 'libs/form/src/lib/types/form-field-config.type';

import { LprAggregationsAPI } from '../../api/services/lpr-aggregations.service';

@Component({
  selector: 'fs-lpr-camera-stats-dialog',
  templateUrl: './lpr-camera-stats-dialog.component.html',
  styleUrls: ['./lpr-camera-stats-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LprCameraStatsDialogComponent implements OnInit, OnDestroy {
  public timeStart: Date = new Date();
  public timeEnd: Date = new Date();
  public formattedTimeStart: number = null;
  public formattedTimeEnd: number = null;
  public mode: string = 'day';
  public minDate: any = null;
  public options: object = {};
  public minDateTime: Date = new Date();

  public loading: boolean = false;

  public form: UntypedFormGroup = new UntypedFormGroup({});
  public formModel: any = {};
  public formFields: SnFormFieldConfig[] = [];

  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly _lprAggregationsAPI: LprAggregationsAPI,
    private readonly _change: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.minDate = new Date();
    this.minDate = addWeeks(this.minDate, -52);
    this._createForm();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public submit(): void {
    if (this.mode !== 'minute') {
      this.timeEnd = this.form.value.date;
    }
    if (this.mode === 'minute') {
      this.timeEnd = this.form.value.time;
    }
    switch (this.mode) {
      case 'day':
        this.timeEnd = addHours(this.timeEnd, 4);
        this.timeStart = addWeeks(this.timeEnd, -2);
        this.formattedTimeStart = this.timeStart
          ? Math.floor(this.timeStart.getTime() / 1000)
          : null;
        this.formattedTimeEnd = this.timeEnd ? Math.floor(this.timeEnd.getTime() / 1000) : null;
        this.timeStart = new Date();
        this.timeEnd = new Date();
        break;
      case 'hour':
        this.timeStart = addDays(this.timeEnd, -1);
        this.formattedTimeStart = this.timeStart
          ? Math.floor(this.timeStart.getTime() / 1000)
          : null;
        this.formattedTimeEnd = this.timeEnd ? Math.floor(this.timeEnd.getTime() / 1000) : null;
        this.timeStart = new Date();
        this.timeEnd = new Date();
        break;
      case 'minute':
        this.timeStart = addHours(this.timeEnd, -2);
        this.formattedTimeStart = this.timeStart
          ? Math.floor(this.timeStart.getTime() / 1000)
          : null;
        this.formattedTimeEnd = this.timeEnd ? Math.floor(this.timeEnd.getTime() / 1000) : null;
        this.timeStart = new Date();
        this.timeEnd = new Date();
    }
    this._lprAggregationsAPI
      .getLprResultsPerCamera(
        this.data.cameraName,
        this.form.value.mode,
        this.formattedTimeEnd,
        this.formattedTimeStart,
      )
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => {
        this.data.results = res;
        this._change.detectChanges();
      });
  }

  private _createForm(): void {
    this.formFields = [
      {
        key: 'mode',
        type: 'select',
        defaultValue: 'day',
        templateOptions: {
          change: (value) => {
            const minDateField = value.parent.fieldGroup.find((field) => field.key === 'date');
            switch (value.formControl.value) {
              case 'day':
                this.mode = 'day';
                this.minDate = new Date();
                this.minDate = addWeeks(this.minDate, -52);
                this.options = {
                  min: this.minDate,
                  max: new Date(),
                };
                minDateField.templateOptions.datepickerOptions.min = this.minDate;
                this._change.detectChanges();
                break;
              case 'hour':
                this.mode = 'hour';
                this.minDate = new Date();
                this.minDate = addWeeks(this.minDate, -8);
                this.options = {
                  min: this.minDate,
                  max: new Date(),
                };
                minDateField.templateOptions.datepickerOptions.min = this.minDate;
                this._change.detectChanges();
                break;
              case 'minute':
                this.mode = 'minute';
                this._change.detectChanges();
                break;
              default:
                break;
            }
          },
          label: t('vehicleIntelligence.mode'),
          required: true,
          options: [
            { label: t('settings.dashboard.units.days'), value: 'day' },
            { label: t('vehicleIntelligence.hours'), value: 'hour' },
          ],
        },
      },
      {
        key: 'date',
        type: 'datepicker',
        defaultValue: new Date(),
        templateOptions: {
          label: t('common.date'),
          appearance: 'fill',
          required: true,
          datepickerOptions: {
            min: this.minDate,
            max: new Date(),
          },
        },
        hideExpression: () => this.mode === 'minute',
      },
      {
        key: 'time',
        type: 'datetimepicker',
        templateOptions: {
          placeholder: t('vehicleIntelligence.dateAndTime'),
          minDate: () => addHours(this.minDateTime, -20),
          maxDate: () => new Date(),
        },
        hideExpression: () => this.mode !== 'minute',
      },
    ];
  }
}
